import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useUser } from 'builder/hooks/useUser'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'

import { FetchStatuses } from 'builder/modules/constants'
import { actions as uiActions, selectors as uiSelectors } from 'builder/modules/ui'
import { actions as userActions, selectors as userSelectors } from 'builder/modules/user'
import { actions as cpActions, selectors as cpSelectors } from 'builder/modules/careerProfile'
import { useCareerProfileEditorState } from 'builder/views/CareerProfile/components/CareerProfileEditorView/hooks/useCareerProfileEditorState'

import ModalOverlay from 'builder/components/ModalOverlay'
import DrawerModal from 'builder/components/DrawerModal/DrawerModal'

import { usePostRedirectUrl } from '../hooks/useGetRedirectUrl'
import { FullNameFormData } from './types'
import { FullNameForm } from './FullNameForm'
import { JobTitleForm } from './JobTitleForm'
import * as Styles from './styles'

export const FullNameModal = () => {
  const user = useUser()
  const dispatch = useDispatch()
  const { isPhone } = useMediaQueries()
  const { mutateAndRedirect } = usePostRedirectUrl()
  const { handleSimpleFieldUpdate, localProfile } = useCareerProfileEditorState()

  const form = useForm<FullNameFormData>({
    mode: 'onChange',
    defaultValues: { firstName: user?.firstName, lastName: user?.lastName },
  })

  const isUserUpdating = useTypedSelector(userSelectors.isUpdating)
  const updateProfileDataStatus = useTypedSelector(cpSelectors.updateProfileDataStatus)
  const { open: isOpen, modal: modalType, cmsgid } = useTypedSelector(uiSelectors.futureLearnModal)
  const isProfileUpdating = updateProfileDataStatus === FetchStatuses.loading

  const handleClose = () => {
    dispatch(uiActions.toggleFutureLearnFullNameModal({ open: false }))
  }

  const onSubmitFullName = (data: FullNameFormData) => {
    dispatch(
      userActions.updateUserRequest({
        email: user?.email,
        firstName: data.firstName,
        lastName: data.lastName,
      }),
    )
  }

  const handleSave = () => {
    dispatch(
      cpActions.updateProfileData({
        targetRoles: localProfile?.targetRoles,
      }),
    )
  }

  useEffect(() => {
    if (!isOpen || isProfileUpdating) return

    if (updateProfileDataStatus === FetchStatuses.loaded) {
      dispatch(cpActions.setUpdateProfileDataStatus(FetchStatuses.notAsked))
      mutateAndRedirect({ cmsgid })
      handleClose()
      return
    }

    if (isUserUpdating) {
      form.reset()
    } else {
      mutateAndRedirect({ cmsgid })
      handleClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserUpdating, updateProfileDataStatus])

  if (isPhone) {
    return (
      <DrawerModal open={isOpen} onClose={handleClose}>
        <Styles.MobileHandle />
        {modalType === 'job_title' && (
          <JobTitleForm
            localProfile={localProfile}
            isProfileUpdating={isProfileUpdating}
            onClose={handleClose}
            onSubmit={handleSave}
            onUpdate={handleSimpleFieldUpdate}
          />
        )}
        {modalType === 'full_name' && (
          <FullNameForm
            form={form}
            isUserUpdating={isUserUpdating}
            onSubmit={onSubmitFullName}
            onClose={handleClose}
          />
        )}
      </DrawerModal>
    )
  }

  return (
    isOpen && (
      <ModalOverlay
        onClose={handleClose}
        backgroundOpacity={0.8}
        overlayFadeDuration={350}
        contentSlideDuration={350}
      >
        {modalType === 'job_title' && (
          <JobTitleForm
            localProfile={localProfile}
            isProfileUpdating={isProfileUpdating}
            onClose={handleClose}
            onSubmit={handleSave}
            onUpdate={handleSimpleFieldUpdate}
          />
        )}
        {modalType === 'full_name' && (
          <FullNameForm
            form={form}
            isUserUpdating={isUserUpdating}
            onSubmit={onSubmitFullName}
            onClose={handleClose}
          />
        )}
      </ModalOverlay>
    )
  )
}
